import React from "react";
import { TrendingUp } from "lucide-react";
import formatValue from "../../../utils/formatValue";
import MetricInfoTooltip from "../components/MetricInfoTooltip";
import {
  FIPE_ZAP_VALUATION_INFO_LABEL,
  FIPE_ZAP_VALUATION_TOOLTIP_TITLE,
  getFipeZapValuationTooltipDescription,
} from "../constants/fipeZapValuationTooltip";
import {
  MARKET_VALUE_YIELD_INFO_LABEL,
  MARKET_VALUE_YIELD_TOOLTIP_TITLE,
  getMarketValueSourceHint,
  getMarketValueYieldTooltipDescription,
} from "../constants/marketValueYieldTooltip";
import { IPropertyReturn, YieldBasis } from "../types/ownerDashboard.types";
import { formatPercentage } from "../utils/formatPercentage";
import {
  formatPropertyReturnDate,
  getPropertyReturnReferenceDate,
} from "../utils/formatPropertyReturnDate";
import PropertyReturnCardMenu from "./PropertyReturnCardMenu";

interface PropertyReturnCardProps {
  propertyReturn: IPropertyReturn;
  propertyLabel: string;
  yieldBasis: YieldBasis;
  onUpdatePurchaseDate: () => void;
}

function getYieldLabels(yieldBasis: YieldBasis) {
  if (yieldBasis === "market_value") {
    return {
      primary: "Rendimento anual (sobre valor de mercado)",
      secondary: "Sobre valor de aquisição",
      secondaryValue: "dividend_on_cost_pct" as const,
    };
  }

  return {
    primary: "Rendimento anual (sobre valor de aquisição)",
    secondary: "Sobre valor de mercado",
    secondaryValue: "dividend_on_market_pct" as const,
  };
}

export default function PropertyReturnCard({
  propertyReturn,
  propertyLabel,
  yieldBasis,
  onUpdatePurchaseDate,
}: PropertyReturnCardProps) {
  const appreciationIsPositive = propertyReturn.appreciation_pct >= 0;
  const referenceDate = getPropertyReturnReferenceDate(
    propertyReturn.purchase_date,
    propertyReturn.valuation_since_date,
    propertyReturn.purchase_value_registered_at
  );
  const yieldLabels = getYieldLabels(yieldBasis);
  const secondaryYield = propertyReturn[yieldLabels.secondaryValue];
  const isMarketBasis = yieldBasis === "market_value";
  const marketValueHint = getMarketValueSourceHint(propertyReturn.market_value_source);
  const displayedMarketValue = isMarketBasis
    ? propertyReturn.effective_market_value
    : propertyReturn.estimated_current_value;

  return (
    <article className="flex flex-col gap-3 rounded-2xl border border-white/[0.08] bg-[#1c2128] p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="line-clamp-2 text-xs font-semibold text-[#8b949e]">
            {propertyLabel}
          </p>
          <p className="mt-1 text-sm text-[#6e7681]">
            {referenceDate.label} {formatPropertyReturnDate(referenceDate.date)}
          </p>
        </div>

        <PropertyReturnCardMenu onUpdatePurchaseDate={onUpdatePurchaseDate} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="flex items-center gap-1">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]">
              {isMarketBasis ? "Valor de mercado" : "Valor estimado"}
            </p>
            {isMarketBasis && (
              <MetricInfoTooltip
                variant="subtle"
                title={MARKET_VALUE_YIELD_TOOLTIP_TITLE}
                description={getMarketValueYieldTooltipDescription(
                  propertyReturn.market_value_source
                )}
                ariaLabel={MARKET_VALUE_YIELD_INFO_LABEL}
              />
            )}
          </div>
          <p className="mt-1 text-sm font-bold text-[#f0f6fc]">
            {formatValue(displayedMarketValue)}
          </p>
          {isMarketBasis && marketValueHint && (
            <p className="mt-1 text-[0.65rem] text-[#6e7681]">{marketValueHint}</p>
          )}
        </div>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]">
              Valorização FIPE Zap
            </p>
            <MetricInfoTooltip
              variant="subtle"
              title={FIPE_ZAP_VALUATION_TOOLTIP_TITLE}
              description={getFipeZapValuationTooltipDescription(propertyReturn.fipe_zap_city)}
              ariaLabel={FIPE_ZAP_VALUATION_INFO_LABEL}
            />
          </div>
          <p
            className={`mt-1 flex items-center gap-1 text-sm font-bold ${
              appreciationIsPositive ? "text-[#3fb950]" : "text-[#f85149]"
            }`}
          >
            {appreciationIsPositive && <TrendingUp className="h-3.5 w-3.5" />}
            {formatPercentage(propertyReturn.appreciation_pct)}
          </p>
        </div>
        <div className="col-span-2">
          <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#6e7681]">
            {yieldLabels.primary}
          </p>
          <p className="mt-1 text-sm font-bold text-[#6ea8ff]">
            {formatPercentage(propertyReturn.annual_rent_yield_pct)}
          </p>
          <p className="mt-1 text-xs text-[#6e7681]">
            {yieldLabels.secondary}: {formatPercentage(secondaryYield)}
          </p>
        </div>
      </div>

      <div className="space-y-1 text-xs text-[#6e7681]">
        <p>Valor de aquisição: {formatValue(propertyReturn.purchase_value)}</p>
        {propertyReturn.market_value != null && (
          <p>Valor de mercado cadastrado: {formatValue(propertyReturn.market_value)}</p>
        )}
      </div>
    </article>
  );
}
